import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsx mdx */

import DefaultLayout from "C:/Projekte/Experimente/github/react-gsap/node_modules/gatsby-theme-docz/src/base/Layout.js";
import { Fragment } from 'react';
import { Playground, Props } from 'docz';
import { Controls, PlayState, ScrollTrigger } from './../../../react-gsap/src/';
import { Tween } from './Tween';
import { Timeline, TargetWithNames } from './Timeline';
export const _frontmatter = {};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">



    <h1 {...{
      "id": "scrolltrigger"
    }}>{`ScrollTrigger`}</h1>
    <p>{`The ScrollTrigger component is a small helper for the ScrollTrigger plugin.`}</p>
    <p>{`Read the official documentation: `}<a parentName="p" {...{
        "href": "https://greensock.com/docs/v3/Plugins/ScrollTrigger"
      }}>{`https://greensock.com/docs/v3/Plugins/ScrollTrigger`}</a></p>
    <p>{`It's available since version 3.2.0.
Before you also could use the ScrollTrigger plugin by importing/registering and using it in a Tween or Timeline by yourself:`}</p>
    <pre><code parentName="pre" {...{}}>{`import { Tween } from 'react-gsap';

import { ScrollTrigger } from 'gsap/dist/ScrollTrigger';
gsap.registerPlugin(ScrollTrigger);

<Tween
  to={{
    x: '300px',
    scrollTrigger: {
      trigger: '.square',
      start: '-200px center',
      end: '200px center',
      scrub: 0.5,
      markers: true,
    },
  }}
>
  <div className="square" style={{ width: '100px', height: '100px', background: '#ccc' }} />
</Tween>
`}</code></pre>
    <h2 {...{
      "id": "basic-usage"
    }}>{`Basic usage`}</h2>
    <p>{`With the ScrollTrigger component, it looks like the following. If you don't add a trigger prop, it will use the ref from the Tween target.`}</p>
    <Playground __position={0} __code={'<ScrollTrigger start=\"-200px center\" end=\"200px center\" scrub={0.5} markers>\n  <Tween\n    to={{\n      x: \'300px\',\n    }}\n  >\n    <div style={{ width: \'100px\', height: \'100px\', background: \'#ccc\' }} />\n  </Tween>\n  <Tween\n    to={{\n      x: \'300px\',\n    }}\n  >\n    <div style={{ width: \'100px\', height: \'100px\', background: \'#999\' }} />\n  </Tween>\n</ScrollTrigger>'} __scope={{
      props,
      DefaultLayout,
      Fragment,
      Playground,
      Props,
      Controls,
      PlayState,
      ScrollTrigger,
      Tween,
      Timeline,
      TargetWithNames,
      DefaultLayout,
      _frontmatter
    }} mdxType="Playground">
  <ScrollTrigger start="-200px center" end="200px center" scrub={0.5} markers mdxType="ScrollTrigger">
    <Tween to={{
          x: '300px'
        }} mdxType="Tween">
      <div style={{
            width: '100px',
            height: '100px',
            background: '#ccc'
          }} />
    </Tween>
    <Tween to={{
          x: '300px'
        }} mdxType="Tween">
      <div style={{
            width: '100px',
            height: '100px',
            background: '#999'
          }} />
    </Tween>
  </ScrollTrigger>
    </Playground>
    <h2 {...{
      "id": "use-trigger-prop"
    }}>{`Use "trigger" prop`}</h2>
    <p>{`Currently it's not possible to change the props on the fly. `}<strong parentName="p">{`So this will not work for the `}<inlineCode parentName="strong">{`trigger`}</inlineCode>{` prop`}</strong>{`:`}</p>
    <pre><code parentName="pre" {...{}}>{`const triggerRef = useRef(null);
const [trigger, setTrigger] = useState(triggerRef.current);

useEffect(() => {
  setTrigger(triggerRef.current);
}, []);

return (
  <>
    <ScrollTrigger trigger={trigger}>
      <Tween
        to={{
          x: '500px',
        }}
      >
        <div>This element gets not tweened by ref</div>
      </Tween>
    </ScrollTrigger>

    <Square ref={triggerRef}>
      This element is the trigger
    </Square>
  </>
);
`}</code></pre>
    <p>{`If you want to target a ref directly instead of using a CSS selector you can use a Timeline with a forwardRef target:`}</p>
    <pre><code parentName="pre" {...{}}>{`// This is the target component which "exports" 4 refs
const TargetWithNames = forwardRef((props, ref: any) => {
  const div1 = useRef(null);
  const div2 = useRef<MutableRefObject<any>[]>([]);
  const div3 = useRef(null);
  const trigger = useRef(null);

  useImperativeHandle(ref, () => ({
    div1,
    div2,
    div3,
    trigger,
  }));

  return (
    <div ref={trigger} style={{ textAlign: 'center' }}>
      <h3 ref={div1}>THIS</h3>
      <SplitChars
        ref={(charRef: MutableRefObject<any>) => div2.current.push(charRef)}
        wrapper={<h3 style={{ display: 'inline-block' }} />}
      >
        TEST
      </SplitChars>
      <h3 ref={div3}>IS A</h3>
    </div>
  );
});
`}</code></pre>
    <p>{`You can then use the key of the exported refs in the `}<inlineCode parentName="p">{`trigger`}</inlineCode>{` or `}<inlineCode parentName="p">{`target`}</inlineCode>{` props.
If it doesn't find a ref with this key it will use the `}<inlineCode parentName="p">{`trigger`}</inlineCode>{` string as CSS selector.`}</p>
    <Playground __position={1} __code={'<ScrollTrigger\n  trigger=\"trigger\"\n  start=\"top center\"\n  end=\"400px center\"\n  scrub={0.5}\n  markers={true}\n  pin={true}\n>\n  <Timeline target={<TargetWithNames />}>\n    <Tween\n      from={{\n        x: \'-100vw\',\n      }}\n      target=\"div1\"\n    />\n    <Tween\n      from={{\n        x: \'-100vw\',\n      }}\n      target=\"div3\"\n    />\n    <Tween\n      from={{\n        x: \'-100vw\',\n      }}\n      stagger={0.5}\n      target=\"div2\"\n    />\n  </Timeline>\n</ScrollTrigger>'} __scope={{
      props,
      DefaultLayout,
      Fragment,
      Playground,
      Props,
      Controls,
      PlayState,
      ScrollTrigger,
      Tween,
      Timeline,
      TargetWithNames,
      DefaultLayout,
      _frontmatter
    }} mdxType="Playground">
  <ScrollTrigger trigger="trigger" start="top center" end="400px center" scrub={0.5} markers={true} pin={true} mdxType="ScrollTrigger">
    <Timeline target={<TargetWithNames mdxType="TargetWithNames" />} mdxType="Timeline">
      <Tween from={{
            x: '-100vw'
          }} target="div1" mdxType="Tween" />
      <Tween from={{
            x: '-100vw'
          }} target="div3" mdxType="Tween" />
      <Tween from={{
            x: '-100vw'
          }} stagger={0.5} target="div2" mdxType="Tween" />
    </Timeline>
  </ScrollTrigger>
    </Playground>
    <h2 {...{
      "id": "standalone"
    }}>{`Standalone`}</h2>
    <p>{`If you don't pass children to the component a GSAP ScrollTrigger instance will be created and can be used standalone.`}</p>
    <p>{`You can get the instance by calling `}<inlineCode parentName="p">{`getGSAP()`}</inlineCode>{` on the ref.`}</p>
    <Playground __position={2} __code={'() => {\n  const scrollTrigger = React.useRef(null)\n  return (\n    <Fragment>\n      <div\n        id=\"id\"\n        style={{ width: \'100px\', height: \'100px\', background: \'#ccc\' }}\n      />\n      <div\n        id=\"otherID\"\n        style={{ width: \'100px\', height: \'100px\', background: \'#999\' }}\n      />\n      <ScrollTrigger\n        ref={scrollTrigger}\n        trigger=\"#id\"\n        start=\"top top\"\n        endTrigger=\"#otherID\"\n        end=\"bottom 50%+=100px\"\n        markers\n        onToggle={self => console.log(\'toggled, isActive:\', self.isActive)}\n        onUpdate={self => {\n          console.log(\n            \'progress:\',\n            self.progress.toFixed(3),\n            \'direction:\',\n            self.direction,\n            \'velocity\',\n            self.getVelocity(),\n          )\n        }}\n      />\n      <button onClick={() => scrollTrigger.current.getGSAP().disable()}>\n        Disable ScrollTrigger\n      </button>\n    </Fragment>\n  )\n}'} __scope={{
      props,
      DefaultLayout,
      Fragment,
      Playground,
      Props,
      Controls,
      PlayState,
      ScrollTrigger,
      Tween,
      Timeline,
      TargetWithNames,
      DefaultLayout,
      _frontmatter
    }} mdxType="Playground">
  {() => {
        const scrollTrigger = React.useRef(null);
        return <Fragment mdxType="Fragment">
        <div id="id" style={{
            width: '100px',
            height: '100px',
            background: '#ccc'
          }} />
        <div id="otherID" style={{
            width: '100px',
            height: '100px',
            background: '#999'
          }} />
        <ScrollTrigger ref={scrollTrigger} trigger="#id" start="top top" endTrigger="#otherID" end="bottom 50%+=100px" markers onToggle={self => console.log("toggled, isActive:", self.isActive)} onUpdate={self => {
            console.log("progress:", self.progress.toFixed(3), "direction:", self.direction, "velocity", self.getVelocity());
          }} mdxType="ScrollTrigger" />
        <button onClick={() => scrollTrigger.current.getGSAP().disable()}>Disable ScrollTrigger</button>
      </Fragment>;
      }}
    </Playground>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      